import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact" | "flexible";
          callback?: (token: string) => void;
          "error-callback"?: () => boolean;
          "expired-callback"?: () => void;
          "timeout-callback"?: () => void;
          "unsupported-callback"?: () => void;
        },
      ) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let turnstileLoader: Promise<void> | null = null;

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (turnstileLoader) return turnstileLoader;

  turnstileLoader = new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      turnstileLoader = null;
      reject(new Error("Turnstile script timed out."));
    }, 15000);
    const resolveLoaded = () => {
      window.clearTimeout(timeout);
      resolve();
    };
    const rejectLoad = () => {
      window.clearTimeout(timeout);
      turnstileLoader = null;
      reject(new Error("Turnstile script failed to load."));
    };
    const existing = document.querySelector<HTMLScriptElement>(
      "script[data-ktd-turnstile='true'], script[src^='https://challenges.cloudflare.com/turnstile/v0/api.js']",
    );
    if (existing) {
      existing.addEventListener("load", resolveLoaded, { once: true });
      existing.addEventListener("error", rejectLoad, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.dataset.ktdTurnstile = "true";
    script.addEventListener("load", resolveLoaded, { once: true });
    script.addEventListener("error", rejectLoad, { once: true });
    document.head.append(script);
  });

  return turnstileLoader;
}

export function TurnstileWidget({ siteKey }: { siteKey: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const renderedRef = useRef(false);
  const [token, setToken] = useState("");
  const [status, setStatus] = useState<"loading" | "ready" | "verified" | "error" | "expired">("loading");

  useEffect(() => {
    renderedRef.current = false;
    setToken("");
    setStatus("loading");
    if (ref.current) ref.current.innerHTML = "";
  }, [siteKey]);

  useEffect(() => {
    if (!siteKey || !ref.current || renderedRef.current) return;
    let active = true;

    const renderWidget = async () => {
      try {
        await loadTurnstileScript();
        if (!active || !ref.current || !window.turnstile || renderedRef.current) return;

        try {
          window.turnstile.render(ref.current, {
            sitekey: siteKey,
            theme: "light",
            size: "normal",
            callback: (value) => {
              if (!active) return;
              setToken(value);
              setStatus("verified");
            },
            "error-callback": () => {
              if (active) {
                setToken("");
                setStatus("error");
              }
              return true;
            },
            "expired-callback": () => {
              if (active) {
                setToken("");
                setStatus("expired");
              }
            },
            "timeout-callback": () => {
              if (active) {
                setToken("");
                setStatus("expired");
              }
            },
            "unsupported-callback": () => {
              if (active) {
                setToken("");
                setStatus("error");
              }
            },
          });
          renderedRef.current = true;
          setStatus("ready");
        } catch {
          setToken("");
          setStatus("error");
        }
      } catch {
        setToken("");
        setStatus("error");
      }
    };

    void renderWidget();

    return () => {
      active = false;
    };
  }, [siteKey]);

  const message =
    status === "verified"
      ? "Verification complete. You can submit your upload."
      : status === "error"
        ? "Verification could not load. Please refresh or try another browser."
        : status === "expired"
          ? "Verification expired. Please complete it again before submitting."
          : "Complete this verification before submitting your upload.";

  return (
    <section className="turnstile-panel rounded-lg border border-ktd-gold/35 bg-[#fffdf8] p-4 text-ktd-rich shadow-sm sm:p-5">
      <input type="hidden" name="cf-turnstile-response" value={token} />
      <div className="mb-3">
        <p className="text-sm font-extrabold text-ktd-rich">Upload verification</p>
        <p className="mt-1 text-xs font-semibold leading-5 text-[#4d4234]">
          This quick check helps protect the Memory Media Vault before your files are submitted.
        </p>
      </div>
      <div className="turnstile-frame luxury-scrollbar overflow-x-auto rounded-lg border border-ktd-gold/20 bg-white p-3">
        <div ref={ref} aria-label="Cloudflare Turnstile verification" />
        {status === "loading" && (
          <div className="grid min-h-[68px] place-items-center rounded-md bg-[#fff9ef] px-4 text-center text-xs font-bold leading-5 text-[#4d4234]">
            Loading secure verification...
          </div>
        )}
      </div>
      <p
        className={`mt-3 text-xs font-bold leading-5 ${
          status === "error" || status === "expired" ? "text-[#8a1f1f]" : "text-[#4d4234]"
        }`}
        aria-live="polite"
      >
        {message}
      </p>
    </section>
  );
}
